import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import TitleField from './TitleField';
import GenreSelector from './GenreSelector';
import CoverImageUpload from './CoverImageUpload';
import ContentEditor from './ContentEditor';
import SubmitActions from './SubmitActions';

export interface StoryFormData {
  title: string;
  genre: string;
  content: string;
}

const INITIAL_FORM: StoryFormData = { title: '', genre: '', content: '' };

export default function StoryForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('draft');
  const editId = searchParams.get('edit');
  const storyId = editId || draftId; // the ID to load and update

  const [form, setForm] = useState<StoryFormData>(INITIAL_FORM);
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<StoryFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Load existing story if ?draft=id or ?edit=id is present
  useEffect(() => {
    if (!storyId) return;
    setLoadingDraft(true);
    supabase
      .from('stories')
      .select('title, genre, content, cover_image')
      .eq('id', storyId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!error && data) {
          setForm({
            title: data.title ?? '',
            genre: data.genre ?? '',
            content: data.content ?? '',
          });
          setCoverImage(data.cover_image ?? null);
        }
        setLoadingDraft(false);
      });
  }, [storyId]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const validate = (status: 'published' | 'draft'): boolean => {
    const newErrors: Partial<StoryFormData> = {};
    if (!form.title.trim()) newErrors.title = 'Title is required.';
    else if (form.title.trim().length < 3) newErrors.title = 'Title must be at least 3 characters.';
    if (!form.genre) newErrors.genre = 'Please select a genre.';
    if (!form.content.trim()) newErrors.content = 'Story content is required.';
    else if (form.content.trim().length < 100) newErrors.content = 'Story must be at least 100 characters.';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    if (status === 'published' && !coverImage) {
      showToast('error', 'A cover image is required to publish. Please upload one or save as a draft.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (status: 'published' | 'draft') => {
    if (!validate(status)) return;
    if (!user) {
      showToast('error', 'You must be signed in to submit a story.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        genre: form.genre,
        content: form.content.trim(),
        cover_image: coverImage ?? null,
        status,
        published_date: status === 'published' ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      };

      if (storyId) {
        // Update existing story (draft or published)
        const { error } = await supabase
          .from('stories')
          .update(payload)
          .eq('id', storyId)
          .eq('author_id', user.id);
        if (error) throw error;
      } else {
        // Insert new story
        const { error } = await supabase.from('stories').insert({
          ...payload,
          author_id: user.id,
          read_count: 0,
          like_count: 0,
        });
        if (error) throw error;
      }

      showToast('success', status === 'published' ? 'Story published successfully!' : 'Draft saved!');
      setTimeout(() => navigate('/profile/writer'), 1500);
    } catch (err: any) {
      showToast('error', err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const wordCount = form.content.trim() ? form.content.trim().split(/\s+/).length : 0;
  const charCount = form.content.length;

  if (loadingDraft) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-400" style={{ fontFamily: 'Crimson Text, serif' }}>
        <i className="ri-loader-4-line animate-spin text-2xl mr-3 text-red-500"></i>
        {editId ? 'Loading your story...' : 'Loading your draft...'}
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-24 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded-lg shadow-2xl border transition-all duration-300 ${
            toast.type === 'success'
              ? 'bg-zinc-900 border-green-700/50 text-green-400'
              : 'bg-zinc-900 border-red-700/50 text-red-400'
          }`}
        >
          <i className={`text-lg ${toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}`}></i>
          <span className="text-sm font-medium">{toast.message}</span>
        </div>
      )}

      {editId && (
        <div className="flex items-center gap-2 mb-6 px-4 py-3 bg-red-900/20 border border-red-700/30 rounded-lg text-red-300 text-sm" style={{ fontFamily: 'Crimson Text, serif' }}>
          <i className="ri-edit-line text-base"></i>
          Editing an existing story — your saved content has been loaded.
        </div>
      )}

      {draftId && !editId && (
        <div className="flex items-center gap-2 mb-6 px-4 py-3 bg-amber-900/20 border border-amber-700/30 rounded-lg text-amber-400 text-sm" style={{ fontFamily: 'Crimson Text, serif' }}>
          <i className="ri-draft-line text-base"></i>
          Continuing from a saved draft — your previous content has been loaded.
        </div>
      )}

      <div className="space-y-8">
        <TitleField
          value={form.title}
          onChange={(v) => setForm((f) => ({ ...f, title: v }))}
          error={errors.title}
        />

        <GenreSelector
          value={form.genre}
          onChange={(v) => setForm((f) => ({ ...f, genre: v }))}
          error={errors.genre}
        />

        <CoverImageUpload
          value={coverImage}
          onChange={setCoverImage}
        />

        <ContentEditor
          value={form.content}
          onChange={(v) => setForm((f) => ({ ...f, content: v }))}
          error={errors.content}
          wordCount={wordCount}
          charCount={charCount}
        />

        <SubmitActions
          onPublish={() => handleSubmit('published')}
          onSaveDraft={() => handleSubmit('draft')}
          onDiscard={() => navigate('/profile/writer')}
          submitting={submitting}
        />
      </div>
    </div>
  );
}
